const API_BASE = '/api/geo';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

/**
 * Run a full GEO scan across multiple prompts
 * @param {string} domain - The domain to scan
 * @param {string[]} prompts - Array of prompts to scan
 * @param {string[]} engines - AI engines to use
 * @returns {Promise<Object>} Full scan results with dashboard metrics
 */
export const runGeoScan = async (domain, prompts, engines = ['google_ai', 'perplexity', 'chatgpt']) => {
  const response = await fetch(`${API_BASE}/scan`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ domain, prompts, engines })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'GEO scan failed');
  }
  return response.json();
};

/**
 * Run a single prompt GEO scan
 * @param {string} domain - The domain to scan
 * @param {string} prompt - Single prompt to scan
 * @param {string[]} engines - AI engines to use
 * @returns {Promise<Object>} Single prompt scan results
 */
export const runSingleScan = async (domain, prompt, engines = ['google_ai', 'perplexity', 'chatgpt']) => {
  const response = await fetch(`${API_BASE}/scan-single`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ domain, prompt, engines })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'GEO scan failed');
  }
  return response.json();
};

/**
 * Get usage stats for the current user
 * @returns {Promise<Object>} Usage data (monthly/daily API calls)
 */
/**
 * Latest saved GEO scan for a domain (null when none).
 */
export const getGeoLatest = async (domain) => {
  const response = await fetch(`${API_BASE}/latest/${encodeURIComponent(domain)}`, { headers: authHeaders() });
  if (!response.ok) return null;
  const result = await response.json();
  return result && result.found ? result : null;
};

/** Saved page state (tracked prompts, last analysis) for a domain. */
export const getGeoState = async (domain, key) => {
  const response = await fetch(`${API_BASE}/state/${encodeURIComponent(domain)}/${encodeURIComponent(key)}`, { headers: authHeaders() });
  if (!response.ok) return null;
  const result = await response.json();
  return result && result.found ? result.data : null;
};
export const saveGeoState = async (domain, key, data) => {
  const response = await fetch(`${API_BASE}/state/${encodeURIComponent(domain)}/${encodeURIComponent(key)}`, {
    method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ data })
  });
  return response.ok;
};

export const getGeoUsage = async () => {
  const response = await fetch(`${API_BASE}/usage`, {
    headers: authHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch usage data');
  const result = await response.json();
  // Unwrap: backend returns { success, data: { monthlyApiCalls, ... } }
  return result.data || result;
};

/**
 * Get available AI engines
 * @returns {Promise<Object>} List of available engines
 */
export const getGeoEngines = async () => {
  const response = await fetch(`${API_BASE}/engines`, {
    headers: authHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch engines');
  const result = await response.json();
  // Unwrap: backend returns { success, data: { engines: [...] } }
  return result.data || result;
};

// Legacy exports for backward compatibility with existing dashboard components
export const getGEODashboard = async (domain, dateRange = '30') => {
  // The new API uses on-demand scanning, so we return empty state
  // Dashboard now triggers scans directly
  return { success: true, data: null, message: 'Use runGeoScan for live data' };
};

export const getPromptVisibility = async (domain, dateRange = '30', filter = 'all') => {
  return { success: true, data: null, message: 'Use runGeoScan for live data' };
};

export const getCitationShare = async (domain, topN = 5) => {
  return { success: true, data: null, message: 'Use runGeoScan for live data' };
};

export const getAnswerSnapshot = async (domain, dateRange = '30', compare = null) => {
  return { success: true, data: null, message: 'Use runGeoScan for live data' };
};

export const getCitationHistory = async (domain, promptId = null, page = 1) => {
  return { success: true, data: null, message: 'Use runGeoScan for live data' };
};

export default {
  runGeoScan,
  runSingleScan,
  getGeoUsage,
  getGeoEngines,
  getGEODashboard,
  getPromptVisibility,
  getCitationShare,
  getAnswerSnapshot,
  getCitationHistory
};
